/**
 * PullToRefresh (PH9-2) — wrap a scrollable region to trigger a refresh
 * when the user pulls down from the top.
 *
 * PointerEvents-based so it works on touch + mouse (desktop drag during
 * QA). Activates only when the wrapped container is scrolled to the top
 * — pulling mid-scroll is a no-op. Releases when threshold reached and
 * fires onRefresh(); shows spinner until the promise resolves.
 *
 * Mobile-only by default (md: hidden? — kept always on so desktop QA
 * can validate without a mobile device). Disable by setting `disabled`.
 */
import { useCallback, useRef, useState } from "react";
import type { ReactNode } from "react";
import { CircleNotch, ArrowDown } from "@phosphor-icons/react";

const PULL_THRESHOLD = 64;
const MAX_PULL = 100;
const RESISTANCE = 0.55;

export interface PullToRefreshProps {
  /** Called when the user pulls past the threshold. Should return a Promise. */
  onRefresh: () => Promise<unknown> | void;
  /** Disable the pull gesture (e.g. when modal is open). */
  disabled?: boolean;
  children: ReactNode;
  /** Optional test id on the outer wrapper. */
  testId?: string;
}

export function PullToRefresh({
  onRefresh,
  disabled = false,
  children,
  testId = "pull-to-refresh",
}: PullToRefreshProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const startY = useRef<number | null>(null);
  const activePointer = useRef<number | null>(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const atTop = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return false;
    return el.scrollTop <= 0;
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled || refreshing) return;
    if (!atTop()) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    startY.current = e.clientY;
    activePointer.current = e.pointerId;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (startY.current == null || activePointer.current !== e.pointerId) return;
    if (!atTop()) {
      startY.current = null;
      setPull(0);
      return;
    }
    const dy = e.clientY - startY.current;
    if (dy <= 0) {
      setPull(0);
      return;
    }
    const damped = Math.min(MAX_PULL, dy * RESISTANCE);
    setPull(damped);
  };

  const onPointerEnd = async (e: React.PointerEvent) => {
    if (activePointer.current !== e.pointerId) return;
    activePointer.current = null;
    const triggered = pull >= PULL_THRESHOLD;
    startY.current = null;
    if (triggered && !refreshing) {
      setRefreshing(true);
      setPull(PULL_THRESHOLD);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPull(0);
      }
    } else {
      setPull(0);
    }
  };

  const overscroll = pull > 0 || refreshing;
  const progress = Math.min(1, pull / PULL_THRESHOLD);

  return (
    <div
      ref={wrapperRef}
      data-testid={testId}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onPointerLeave={onPointerEnd}
      className="overflow-y-auto relative"
      style={{ touchAction: "pan-y" }}
    >
      {overscroll && (
        <div
          className="absolute left-0 right-0 flex items-center justify-center pointer-events-none"
          style={{
            top: 0,
            height: pull,
            transition: refreshing ? "height 200ms ease-out" : "none",
          }}
          aria-hidden="true"
          data-testid={`${testId}-indicator`}
        >
          {refreshing ? (
            <CircleNotch
              size={18}
              className="text-accent animate-spin"
            />
          ) : (
            <ArrowDown
              size={18}
              className="text-fg-muted"
              style={{
                transform: `rotate(${progress * 180}deg)`,
                transition: "transform 100ms ease-out",
              }}
            />
          )}
        </div>
      )}
      <div
        style={{
          transform: overscroll ? `translateY(${pull}px)` : undefined,
          transition: refreshing ? "transform 200ms ease-out" : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
